import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { ColorSwatch } from '@/components/quote/ColorSwatch';
import { paymentsApi } from '@/services/payments';
import { couponsApi, type CouponBenefits } from '@/services/coupons';
import type { PublicMaterial } from '@/services/materials';
import { trackBeginCheckout, trackCheckoutError, stashCheckoutValue } from '@/services/analytics';

interface ProductCheckoutPanelProps {
  readonly productId: string;
  readonly productName: string;
  readonly materialIds: readonly string[];
  readonly allMaterials: readonly PublicMaterial[];
  readonly unitPrice: number;
  readonly estimatedDays?: number;
  readonly onColorChange?: (color: string | null) => void;
}

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

export const ProductCheckoutPanel = ({
  productId,
  productName,
  materialIds,
  allMaterials,
  unitPrice,
  estimatedDays,
  onColorChange,
}: ProductCheckoutPanelProps) => {
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponBenefits | null>(null);

  // Filter to only materials this product supports
  const availableMaterials = useMemo(
    () => allMaterials.filter((m) => materialIds.includes(m.materialId)),
    [allMaterials, materialIds],
  );

  // Auto-select first material if only one available
  const effectiveMaterialId = selectedMaterialId
    ?? (availableMaterials.length === 1 ? availableMaterials[0].materialId : null);

  const handleMaterialChange = (materialId: string) => {
    setSelectedMaterialId(materialId);
    setSelectedColor(null);
    onColorChange?.(null);
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    onColorChange?.(color);
  };

  const rawSubtotal = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);
  const discount = appliedCoupon?.percentOff
    ? Math.round(rawSubtotal * (appliedCoupon.percentOff / 100) * 100) / 100
    : 0;
  const subtotal = Math.round((rawSubtotal - discount) * 100) / 100;

  const canCheckout = termsAccepted && !!effectiveMaterialId && !!selectedColor;

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const result = await couponsApi.validate(couponInput.trim());
      if (result.valid && result.coupon) {
        setAppliedCoupon(result.coupon);
        setCouponError(null);
      } else {
        setCouponError(result.error ?? 'Invalid coupon');
        setAppliedCoupon(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError(null);
  };

  const handleCheckout = async () => {
    if (!canCheckout) return;

    setIsLoading(true);
    setError(null);

    trackBeginCheckout({
      value: subtotal,
      checkout_type: 'product',
      quantity,
    });

    try {
      const { sessionUrl } = await paymentsApi.createCheckout({
        type: 'product',
        productId,
        materialId: effectiveMaterialId!,
        color: selectedColor!,
        quantity,
        couponCode: appliedCoupon?.code ?? null,
      });
      await stashCheckoutValue(subtotal);
      window.location.href = sessionUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      setError(message);
      trackCheckoutError({
        error_message: message,
        checkout_type: 'product',
        value: subtotal,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface-sunken)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-secondary)',
          }}
        >
          Order Summary
        </h3>
      </div>

      <div
        style={{
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        {/* Product */}
        <SummaryRow label="Product" value={productName} />

        {/* Material Selection */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Material
          </span>
          {availableMaterials.length === 1 ? (
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
              }}
            >
              {availableMaterials[0].name.toUpperCase()}
            </span>
          ) : (
            <div className="flex flex-wrap" style={{ gap: 'var(--space-2)' }}>
              {availableMaterials.map((mat) => {
                const isSelected = effectiveMaterialId === mat.materialId;
                return (
                  <button
                    key={mat.materialId}
                    type="button"
                    onClick={() => handleMaterialChange(mat.materialId)}
                    style={{
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-1) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      border: isSelected
                        ? '2px solid var(--color-primary-500)'
                        : '1px solid var(--border-default)',
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? 'var(--bg-accent-subtle)'
                        : 'var(--bg-surface)',
                      color: isSelected
                        ? 'var(--text-accent)'
                        : 'var(--text-secondary)',
                      transition: 'all var(--duration-fast) var(--ease-out)',
                    }}
                  >
                    {mat.name.toUpperCase()}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Color Selection */}
        {effectiveMaterialId && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Color
            </span>
            <ColorSwatch
              materials={availableMaterials}
              selectedMaterialId={effectiveMaterialId}
              selectedColor={selectedColor}
              onSelectColor={handleColorChange}
            />
          </div>
        )}

        {/* Quantity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Quantity
          </span>
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(MIN_QUANTITY, q - 1))}
              disabled={quantity <= MIN_QUANTITY}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity <= MIN_QUANTITY ? 'not-allowed' : 'pointer',
                opacity: quantity <= MIN_QUANTITY ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              -
            </button>
            <input
              type="number"
              min={MIN_QUANTITY}
              max={MAX_QUANTITY}
              value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  setQuantity(Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, val)));
                }
              }}
              style={{
                width: '3.5rem',
                textAlign: 'center',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)',
              }}
            />
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}
              disabled={quantity >= MAX_QUANTITY}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity >= MAX_QUANTITY ? 'not-allowed' : 'pointer',
                opacity: quantity >= MAX_QUANTITY ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Coupon Code */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Coupon Code
          </span>
          {appliedCoupon ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-success-subtle, rgba(34,197,94,0.08))',
                border: '1px solid var(--color-success-600, #16a34a)',
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-success-600, #16a34a)',
                    fontFamily: 'monospace',
                  }}
                >
                  {appliedCoupon.code}
                </span>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: '1px' }}>
                  {[
                    appliedCoupon.percentOff ? `${appliedCoupon.percentOff}% off` : null,
                    appliedCoupon.freeShipping ? 'Free shipping' : null,
                  ]
                    .filter(Boolean)
                    .join(' + ')}
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 'var(--space-1)',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex" style={{ gap: 'var(--space-2)' }}>
              <input
                type="text"
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value.toUpperCase());
                  setCouponError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApplyCoupon();
                }}
                placeholder="Enter code"
                style={{
                  flex: 1,
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${couponError ? 'var(--color-error-500)' : 'var(--border-default)'}`,
                  padding: 'var(--space-2) var(--space-2)',
                  minHeight: '36px',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)',
                  textTransform: 'uppercase',
                  fontFamily: 'monospace',
                }}
              />
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponInput.trim()}
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-accent)',
                  background: 'none',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2) var(--space-3)',
                  minHeight: '36px',
                  cursor: couponLoading || !couponInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: couponLoading || !couponInput.trim() ? 0.5 : 1,
                }}
              >
                {couponLoading ? '...' : 'Apply'}
              </button>
            </div>
          )}
          {couponError && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', margin: 0 }}>
              {couponError}
            </p>
          )}
        </div>

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />

        {/* Pricing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {quantity > 1 && (
            <PriceLine label="Unit price" value={`$${unitPrice.toFixed(2)}`} muted />
          )}
          <PriceLine
            label={quantity > 1 ? `Subtotal (x${quantity})` : 'Subtotal'}
            value={`$${rawSubtotal.toFixed(2)}`}
          />
          {discount > 0 && (
            <PriceLine
              label={`Coupon (${appliedCoupon?.percentOff}% off)`}
              value={`-$${discount.toFixed(2)}`}
              muted
            />
          )}
          <PriceLine
            label="Shipping"
            value={appliedCoupon?.freeShipping ? 'Free (coupon)' : 'At checkout'}
            muted
          />
          <PriceLine label="Tax" value="At checkout" muted />
        </div>

        {estimatedDays && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
            }}
          >
            Est. production: {estimatedDays} business day{estimatedDays !== 1 ? 's' : ''}
          </p>
        )}

        {/* Total */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
        <div className="flex justify-between items-center">
          <span
            style={{
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
            }}
          >
            Total
          </span>
          <span
            style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-primary-600)',
            }}
          >
            ${subtotal.toFixed(2)}
          </span>
        </div>

        {/* Terms */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
        <label
          className="flex items-start"
          style={{ gap: 'var(--space-2)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginTop: '2px', flexShrink: 0 }}
          />
          <span style={{ color: 'var(--text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
            I accept the terms of service and refund policy. Printed items are
            non-returnable unless defective.
          </span>
        </label>

        {/* Checkout CTA */}
        <Button
          onClick={handleCheckout}
          disabled={!canCheckout || isLoading}
          size="lg"
          style={{ width: '100%', marginTop: 'var(--space-1)' }}
        >
          {isLoading
            ? 'Redirecting to payment...'
            : !effectiveMaterialId
              ? 'Select a material'
              : !selectedColor
                ? 'Select a color'
                : !termsAccepted
                  ? 'Accept terms to continue'
                  : 'Proceed to Checkout'}
        </Button>

        {error && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error-500)',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        {/* Trust line */}
        <div
          className="flex items-center justify-center"
          style={{
            gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={{ height: '0.875rem', width: '0.875rem' }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span>Secure checkout via Stripe</span>
        </div>
      </div>
    </div>
  );
};

/* ---- helpers ---- */

const SummaryRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </span>
    <span
      className="truncate"
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
      }}
    >
      {value}
    </span>
  </div>
);

const PriceLine = ({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) => (
  <div className="flex justify-between" style={{ fontSize: 'var(--text-sm)' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span
      style={{
        fontWeight: muted ? undefined : 'var(--font-weight-medium)',
        color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)',
        fontSize: muted ? 'var(--text-xs)' : undefined,
      }}
    >
      {value}
    </span>
  </div>
);
